import { useCallback, useState, useEffect, useRef } from "react";
import {
  EditableTableField,
  EditableTableRow,
  generateRowId,
  buildRows,
} from "./EditableTable.model";

export interface UseEditableTableHandlersProps {
  value: EditableTableRow[];
  onChange: (value: EditableTableRow[]) => void;
  fields: EditableTableField[];
}

export function useEditableTableHandlers({
  value,
  onChange,
  fields,
}: UseEditableTableHandlersProps) {
  const [rows, setRows] = useState<EditableTableRow[]>(() =>
    buildRows(value, fields)
  );

  // Only sync with parent when value changes from external source (not from our own updates)
  const isInternalUpdateRef = useRef(false);

  useEffect(() => {
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    setRows(buildRows(value, fields));
  }, [value, fields]);

  // Sync changes back to parent
  const syncToParent = useCallback(
    (newRows: EditableTableRow[]) => {
      isInternalUpdateRef.current = true;
      onChange(newRows);
    },
    [onChange]
  );

  const addRow = useCallback(() => {
    const newRow: EditableTableRow = {
      id: generateRowId(),
      enabled: true,
    };
    fields.forEach((field) => {
      newRow[field.key] = "";
    });

    setRows((prev) => {
      const newRows = [...prev, newRow];
      syncToParent(newRows);
      return newRows;
    });
  }, [fields, syncToParent]);

  const updateRow = useCallback(
    (id: string, updates: Partial<EditableTableRow>) => {
      setRows((prev) => {
        const newRows = prev.map((row) =>
          row.id === id ? { ...row, ...updates } : row
        );
        syncToParent(newRows);
        return newRows;
      });
    },
    [syncToParent]
  );

  const deleteRow = useCallback(
    (id: string) => {
      setRows((prev) => {
        const newRows = prev.filter((row) => row.id !== id);
        syncToParent(newRows);
        return newRows;
      });
    },
    [syncToParent]
  );

  const toggleRow = useCallback(
    (id: string) => {
      setRows((prev) => {
        const newRows = prev.map((row) =>
          row.id === id ? { ...row, enabled: !row.enabled } : row
        );
        syncToParent(newRows);
        return newRows;
      });
    },
    [syncToParent]
  );

  return {
    rows,
    addRow,
    updateRow,
    deleteRow,
    toggleRow,
  };
}
